/**
 * Product Actions Menu Component
 * Dropdown menu for product actions (edit, delete)
 */

import { useState, useRef, useEffect } from 'react';
import './ProductActionsMenu.css';

interface ProductActionsMenuProps {
  productId: string;
  productTitle: string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  isOperating?: boolean;
}

const ProductActionsMenu = ({
  productId,
  productTitle,
  onEdit,
  onDelete,
  isOperating = false,
}: ProductActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleEdit = () => {
    setIsOpen(false);
    onEdit(productId);
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (confirm(`確定要刪除商品「${productTitle}」嗎？此操作無法復原。`)) {
      onDelete(productId);
    }
  };

  if (isOperating) {
    return (
      <div className="product-actions-menu product-actions-menu--operating">
        <span className="product-actions-menu__loading">處理中...</span>
      </div>
    );
  }

  return (
    <div className="product-actions-menu" ref={menuRef}>
      <button
        type="button"
        className="product-actions-menu__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="操作選單"
        aria-expanded={isOpen}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="product-actions-menu__icon"
        >
          <circle cx="10" cy="5" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="15" r="1.5" />
        </svg>
      </button>

      {isOpen && (
        <div className="product-actions-menu__dropdown">
          <button
            type="button"
            className="product-actions-menu__item product-actions-menu__item--edit"
            onClick={handleEdit}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="product-actions-menu__item-icon"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V9" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            編輯
          </button>

          <button
            type="button"
            className="product-actions-menu__item product-actions-menu__item--delete"
            onClick={handleDelete}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="product-actions-menu__item-icon"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            刪除
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductActionsMenu;
